import { Divider, Grid, Skeleton } from '@mui/material';
import { HSpacer, RoutalSearchInput, VSpacer } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExportIcon from '../../../../assets/img/export.svg';
import ClientsPlaceholder from '../../../../assets/img/placeholders/clients_placeholder.svg';
import { ClientsTableColumnKeys } from '../../../../context/client/tableColumns';
import { sortDataByTableColumns, TableColumnsSortConfig } from '../../../../context/shared/tableColumns';
import { Client } from '../../../../domain/client/Client';
import { useRetrieveFlatClients } from '../../../../hooks/client/useRetrieveFlatClients';
import { useSearchClients } from '../../../../hooks/client/useSearchClients';
import { useSetClientsColumns } from '../../../../hooks/client/useSetClientsColumns';
import { useSetSelectedClients } from '../../../../hooks/client/useSetSelectedClients';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveSupportDocs } from '../../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import Button from '../../../../react-components/Button';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import Text from '../../../../react-components/Text';
import eventsPanel, { PageViewPages } from '../../../../services/events';
import { ClientsActionsMenu } from '../ClientsActionsMenu';
import { ExportClientsDialog } from '../ExportClientsDialog';
import { getClientsColumns } from './ClientsListColumns';
import { ClientsListColumnsButton } from './ClientsListColumnsButton';

interface ProjectAssetsClientsListProps {
  searchParameters: { limit: number; offset: number };
  sortClientsInfo: TableColumnsSortConfig<ClientsTableColumnKeys>;
  setSortClientsInfo: (sortData: TableColumnsSortConfig<ClientsTableColumnKeys>) => void;
  setSearchParameters: ({ limit, offset }: { limit: number; offset: number }) => void;
}

export const ProjectAssetsClientsList = ({
  searchParameters,
  sortClientsInfo,
  setSortClientsInfo,
  setSearchParameters,
}: ProjectAssetsClientsListProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user } = useRetrieveUser();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: activeProject } = useRetrieveActiveProject();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();
  const [isExportClientsDialogOpen, setIsExportClientsDialogOpen] = useState<boolean>(false);

  const searchText = searchParams.get(`search_text`) ?? ``;

  const { isLoading, data: clientsPagination } = useSearchClients({
    offset: searchParameters.offset,
    limit: searchParameters.limit,
    sortBy: sortClientsInfo.orderBy,
    sortDirection: sortClientsInfo.order,
    searchText: searchText !== `` ? searchText : undefined,
  });

  const { tableColumns } = useSetClientsColumns();

  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; client: Client } | undefined
  >(undefined);

  const { data: selectedClients, run: setSelectedClients } = useSetSelectedClients();

  const { data: flatClients } = useRetrieveFlatClients();

  useEffect(() => {
    eventsPanel.pageView(PageViewPages.clients);
  }, []);

  const { docs, limit, page, pages, total } = clientsPagination || {};

  return (
    <>
      <Grid container direction="column" style={{ padding: `22px`, height: `100%`, width: `100%` }} wrap="nowrap">
        {isLoading || (total && total > 0) || (!!searchText && searchText !== ``) ? (
          <>
            <Grid container>
              <Grid item container alignItems="center" wrap="nowrap">
                <Grid container style={{ height: `100%`, width: `100%` }} justifyContent="flex-end">
                  <Grid item>
                    <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                      {isLoading ? (
                        <Skeleton variant="rectangular" animation="pulse" height={36} width={259} />
                      ) : (
                        <RoutalSearchInput
                          inputContainerStyles={{
                            width: `260px`,
                          }}
                          fieldValue={searchText}
                          onChange={_.debounce((text: any) => {
                            if (text) {
                              searchParams.set(`search_text`, text);
                            } else {
                              searchParams.delete(`search_text`);
                            }
                            setSearchParams(searchParams);
                          }, 300)}
                          searchLive
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                wrap="nowrap"
                style={{
                  minHeight: `46px`,
                }}
              >
                <Grid item container alignItems="center">
                  {isLoading ? (
                    <Skeleton variant="rectangular" animation="pulse" height={20} width={220} />
                  ) : (
                    <Text
                      variant="body"
                      intlId={`${
                        (selectedClients ?? []).length > 0 ? `clients.header.selected_info` : `clients.header.num_rows`
                      }`}
                      intlValues={{
                        num: (selectedClients ?? []).length,
                        total: total ?? ``,
                      }}
                    />
                  )}

                  <HSpacer small />

                  {(selectedClients ?? []).length > 0 ? (
                    <ClientsActionsMenu
                      type="popover"
                      clients={(clientsPagination?.docs ?? []).filter((doc) => selectedClients.includes(doc.id))}
                      popoverProps={{
                        hideArrow: false,
                        style: {
                          justifyContent: `space-between`,
                        },
                        containerStyle: {
                          width: `240px`,
                        },
                        content: <Text variant="body" style={{ cursor: `pointer` }} intlId="clients.actions" />,
                      }}
                    />
                  ) : null}
                </Grid>

                <Grid item container alignItems="center" justifyContent="flex-end">
                  <ClientsListColumnsButton id="clients-configurable-table-columns" />
                  <HSpacer small />
                  <Button
                    styleType="secondary"
                    disabled={selectedClients.length === 0}
                    onClick={() => setIsExportClientsDialogOpen(true)}
                    style={{
                      height: `35px`,
                      width: `35px`,
                      padding: 0,
                      margin: 0,
                      borderRadius: `5px`,
                    }}
                  >
                    <img alt="" height="20px" src={ExportIcon} draggable="false" />
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Divider />

            <Grid item container style={{ overflowX: `auto`, overflowY: `hidden`, height: `100%`, width: `100%` }}>
              {activeProject && (isLoading || (docs ?? []).length > 0) ? (
                <Grid container style={{ width: `100%`, height: `100%`, position: `relative` }}>
                  <div style={{ height: `100%`, width: `100%`, overflowX: `auto`, position: `absolute` }}>
                    <DataTable
                      loading={isLoading}
                      docs={sortDataByTableColumns({
                        data: docs ?? [],
                        tableColumns,
                        sortInfo: sortClientsInfo,
                      }).map((stop: Client) => {
                        return {
                          doc: stop,
                          checkboxIcon: {
                            element: <div style={{ width: `24px`, height: `24px` }} />,
                          },
                        };
                      })}
                      limit={limit}
                      page={page}
                      pages={pages}
                      total={total ?? 0}
                      noFooter={isLoading}
                      onRowDoubleClick={(doc) => {
                        setRightPanelInfo({
                          detailType: `client`,
                          detailId: `${doc.id}`,
                          detailTab: null,
                        });
                      }}
                      onChangePage={({ offset, limit }) =>
                        setSearchParameters({
                          ...searchParameters,
                          offset,
                          limit,
                        })
                      }
                      hover
                      handleOnContextMenu={(ev: any, client: Client) => {
                        ev.preventDefault();
                        setTableContextualAnchor({
                          screenPosition: { top: ev.clientY, left: ev.clientX },
                          client,
                        });
                      }}
                      selectedIds={selectedClients}
                      onMultipleSelection={({ selectedIds: newSelection, fromGlobalCheckbox }) => {
                        if (fromGlobalCheckbox) {
                          if (selectedClients.length > 0) {
                            setSelectedClients([]);
                          } else {
                            setSelectedClients((flatClients ?? []).map((fS) => fS.id));
                          }
                        } else {
                          setSelectedClients(newSelection ?? []);
                        }
                      }}
                      sortBy={sortClientsInfo.orderBy}
                      sortDirection={sortClientsInfo.order}
                      onColumnSort={(column: any, order: `asc` | `desc`) => {
                        setSortClientsInfo({ order, orderBy: column });
                      }}
                      columns={[
                        ...getClientsColumns({
                          activeProject,
                          tableColumns,
                          onOpenRightPanel: (e, doc) => {
                            e.stopPropagation();
                            setRightPanelInfo({
                              detailType: `client`,
                              detailId: `${doc.id}`,
                              detailTab: null,
                            });
                          },
                        }),
                      ]}
                    />

                    {tableContextualAnchor ? (
                      <ClientsActionsMenu
                        type="contextual"
                        contextAnchor={tableContextualAnchor}
                        setContextAnchor={() => {
                          setTableContextualAnchor(undefined);
                        }}
                        clients={[tableContextualAnchor.client]}
                      />
                    ) : null}
                  </div>
                </Grid>
              ) : (
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  style={{
                    height: `100%`,
                    padding: `32px`,
                  }}
                >
                  <Text variant="body-large" intlId="no_results" />
                </Grid>
              )}
            </Grid>
          </>
        ) : (
          <Grid
            container
            direction="column"
            alignItems="center"
            style={{
              height: `100%`,
              paddingTop: `32px`,
            }}
          >
            <Grid container>
              <Grid item sm={12}>
                <Grid container direction="column" justifyContent="center">
                  <Grid container justifyContent="center">
                    <img alt="" src={ClientsPlaceholder} width="150px" />
                  </Grid>

                  <VSpacer medium />

                  <Grid container direction="column" justifyContent="center" style={{ textAlign: `center` }}>
                    <Text variant="h5" intlId="clients.no_clients.title" />

                    <VSpacer medium />

                    <Text variant="body-large" intlId="clients.no_clients.info" />
                  </Grid>

                  <VSpacer medium />

                  <Grid container justifyContent="center">
                    <Text
                      variant="button"
                      intlId="check_out_the_documentation"
                      onClick={() =>
                        window.open(retrieveDoc({ supportPage: `clients`, language: user?.language.value }), `_blank`)
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      <ExportClientsDialog
        open={isExportClientsDialogOpen}
        onCloseDialog={() => setIsExportClientsDialogOpen(false)}
        selectedClientIds={selectedClients}
      />
    </>
  );
};
